import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  Checkbox,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Autorenew as AutorenewIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { formatDateTimeBR } from '../utils/dateUtils';

const SITUACAO_OPTIONS = [
  'Reposição encaminhada',
  'Impressão',
  'Corte',
  'Finalizado',
] as const;

/** Status que registra a data/hora de finalização. */
const FINALIZED_STATUS = 'Finalizado';

const SITUACAO_COLORS: Record<string, { bg: string; color: string }> = {
  'Reposição encaminhada': { bg: '#fef3c7', color: '#b45309' },
  'Impressão': { bg: '#ede9fe', color: '#6d28d9' },
  'Corte': { bg: '#cffafe', color: '#0e7490' },
  'Finalizado': { bg: '#dcfce7', color: '#15803d' },
};

interface Replacement {
  id: string;
  orderNumber: string;
  situacao: string;
  observation?: string | null;
  finalizedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface FormData {
  orderNumber: string;
  situacao: string;
  observation: string;
}

const emptyForm: FormData = {
  orderNumber: '',
  situacao: 'Reposição encaminhada',
  observation: '',
};

const Reposicao: React.FC = () => {
  const [rows, setRows] = useState<Replacement[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [situacaoFilter, setSituacaoFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });

  const [formDialog, setFormDialog] = useState<{ open: boolean; mode: 'add' | 'edit'; id?: string }>({
    open: false,
    mode: 'add',
  });
  const [form, setForm] = useState<FormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const [situacaoDialog, setSituacaoDialog] = useState<{ open: boolean; id?: string; current?: string }>({
    open: false,
  });

  const [viewDialog, setViewDialog] = useState<{ open: boolean; record?: Replacement }>({ open: false });

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') =>
    setSnackbar({ open: true, message, severity });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getAllOrderReplacements(
        startDate || undefined,
        endDate || undefined,
        situacaoFilter || undefined,
      );
      setRows(data || []);
    } catch (error) {
      console.error('Erro ao carregar reposições:', error);
      showSnackbar('Erro ao carregar dados', 'error');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, situacaoFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = rows.filter((r) => {
    const term = searchTerm.toLowerCase();
    return (
      r.orderNumber.toLowerCase().includes(term) ||
      (r.observation || '').toLowerCase().includes(term)
    );
  });

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) setSelected(new Set(filtered.map((r) => r.id)));
    else setSelected(new Set());
  };
  const handleSelectOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const openAdd = () => {
    setForm(emptyForm);
    setFormDialog({ open: true, mode: 'add' });
  };

  const openEdit = (r: Replacement) => {
    setForm({
      orderNumber: r.orderNumber,
      situacao: r.situacao,
      observation: r.observation || '',
    });
    setFormDialog({ open: true, mode: 'edit', id: r.id });
  };

  const handleSaveForm = async () => {
    if (!form.orderNumber.trim()) {
      showSnackbar('Preencha o número do pedido', 'warning');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        orderNumber: form.orderNumber.trim(),
        situacao: form.situacao,
        observation: form.observation || null,
      };
      if (formDialog.mode === 'add') {
        await apiService.createOrderReplacement(payload);
        showSnackbar('Reposição cadastrada com sucesso!', 'success');
      } else if (formDialog.id) {
        await apiService.updateOrderReplacement(formDialog.id, payload);
        showSnackbar('Reposição atualizada com sucesso!', 'success');
      }
      setFormDialog({ open: false, mode: 'add' });
      loadData();
    } catch (error: any) {
      showSnackbar(error?.response?.data?.error || 'Erro ao salvar', 'error');
    } finally {
      setSaving(false);
    }
  };

  // A data de finalização é gravada/limpa automaticamente pelo backend
  const handleChangeSituacao = async (novaSituacao: string) => {
    if (!situacaoDialog.id) return;
    try {
      await apiService.updateOrderReplacement(situacaoDialog.id, { situacao: novaSituacao });
      setSituacaoDialog({ open: false });
      showSnackbar(
        novaSituacao === FINALIZED_STATUS
          ? 'Finalizado! Data e hora registradas.'
          : 'Situação atualizada!',
        'success',
      );
      loadData();
    } catch (error: any) {
      showSnackbar(error?.response?.data?.error || 'Erro ao atualizar situação', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Deseja excluir este registro?')) return;
    try {
      await apiService.deleteOrderReplacement(id);
      showSnackbar('Registro excluído', 'success');
      loadData();
    } catch {
      showSnackbar('Erro ao excluir', 'error');
    }
  };

  const handleDeleteMultiple = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Excluir ${selected.size} registro(s)?`)) return;
    try {
      await apiService.deleteMultipleOrderReplacements(Array.from(selected));
      showSnackbar(`${selected.size} registro(s) excluído(s)`, 'success');
      setSelected(new Set());
      loadData();
    } catch {
      showSnackbar('Erro ao excluir', 'error');
    }
  };

  const situacaoChip = (situacao: string, onClick?: () => void) => {
    const c = SITUACAO_COLORS[situacao] || { bg: '#e5e7eb', color: '#374151' };
    return (
      <Chip
        label={situacao}
        size="small"
        onClick={onClick}
        sx={{
          backgroundColor: c.bg,
          color: c.color,
          fontWeight: 700,
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? { filter: 'brightness(0.95)' } : undefined,
        }}
      />
    );
  };

  const colSpan = 7;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutorenewIcon sx={{ color: '#D4AF37' }} /> Reposição
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Cadastre e acompanhe reposições até a finalização. A data/hora de finalização é registrada automaticamente.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAdd}
          sx={{ backgroundColor: '#D4AF37', color: '#000', fontWeight: 600, px: 3, py: 1.5, '&:hover': { backgroundColor: '#B8941F' } }}
        >
          Novo
        </Button>
      </Box>

      {/* Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar por nº do pedido ou observação"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Situação</InputLabel>
                <Select value={situacaoFilter} label="Situação" onChange={(e) => setSituacaoFilter(e.target.value)}>
                  <MenuItem value="">Todas</MenuItem>
                  {SITUACAO_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth size="small" label="Data inicial" type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth size="small" label="Data final" type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3} sx={{ display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadData}
                sx={{ borderColor: '#D4AF37', color: '#B8941F', height: 40, '&:hover': { borderColor: '#B8941F', backgroundColor: 'rgba(212,175,55,0.04)' } }}
              >
                Atualizar
              </Button>
              {selected.size > 0 && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteMultiple}
                  sx={{ height: 40, whiteSpace: 'nowrap' }}
                >
                  {selected.size}
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(212, 175, 55, 0.05)' }}>
                <TableCell padding="checkbox" sx={{ px: 1 }}>
                  <Checkbox
                    checked={selected.size === filtered.length && filtered.length > 0}
                    indeterminate={selected.size > 0 && selected.size < filtered.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#B8941F' }}>Nº DO PEDIDO</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#B8941F' }}>SITUAÇÃO</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#B8941F' }}>OBSERVAÇÃO</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#B8941F' }}>DATA/HORA</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#B8941F' }}>FINALIZADO EM</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#B8941F', textAlign: 'center' }}>AÇÕES</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" sx={{ color: '#666' }}>Nenhuma reposição encontrada.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell padding="checkbox" sx={{ px: 1 }}>
                      <Checkbox checked={selected.has(r.id)} onChange={() => handleSelectOne(r.id)} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{r.orderNumber}</TableCell>
                    <TableCell>
                      {situacaoChip(r.situacao, () => setSituacaoDialog({ open: true, id: r.id, current: r.situacao }))}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      <Tooltip title={r.observation || ''}>
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.observation || '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ color: '#666', whiteSpace: 'nowrap' }}>
                      {formatDateTimeBR(r.createdAt)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {r.finalizedAt ? (
                        <Typography variant="body2" sx={{ color: '#15803d', fontWeight: 600 }}>
                          {formatDateTimeBR(r.finalizedAt)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#9ca3af' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <Tooltip title="Visualizar">
                        <IconButton size="small" onClick={() => setViewDialog({ open: true, record: r })} sx={{ color: '#D4AF37' }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => openEdit(r)} sx={{ color: '#B8941F' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => handleDelete(r.id)} sx={{ color: '#ef4444' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Por página"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </Card>

      {/* Dialog: Criar / Editar */}
      <Dialog open={formDialog.open} onClose={() => setFormDialog({ open: false, mode: 'add' })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#D4AF37', color: '#000', fontWeight: 600 }}>
          {formDialog.mode === 'add' ? 'Nova Reposição' : 'Editar Reposição'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Nº do Pedido" required
                value={form.orderNumber}
                onChange={(e) => setForm({ ...form, orderNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Situação</InputLabel>
                <Select value={form.situacao} label="Situação" onChange={(e) => setForm({ ...form, situacao: e.target.value })}>
                  {SITUACAO_OPTIONS.map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Observação" multiline rows={3}
                value={form.observation}
                onChange={(e) => setForm({ ...form, observation: e.target.value })}
              />
            </Grid>
            {form.situacao === FINALIZED_STATUS && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Ao salvar como "Finalizado", a data e hora da finalização serão registradas automaticamente.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setFormDialog({ open: false, mode: 'add' })} sx={{ color: '#666' }}>Cancelar</Button>
          <Button
            onClick={handleSaveForm}
            variant="contained"
            disabled={saving}
            sx={{ backgroundColor: '#D4AF37', color: '#000', '&:hover': { backgroundColor: '#B8941F' } }}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Situação */}
      <Dialog open={situacaoDialog.open} onClose={() => setSituacaoDialog({ open: false })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#D4AF37', color: '#000', fontWeight: 600 }}>Alterar Situação</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {SITUACAO_OPTIONS.map((s) => {
              const c = SITUACAO_COLORS[s];
              const isCurrent = s === situacaoDialog.current;
              return (
                <Button
                  key={s}
                  onClick={() => handleChangeSituacao(s)}
                  sx={{
                    justifyContent: 'flex-start',
                    backgroundColor: c.bg,
                    color: c.color,
                    fontWeight: 700,
                    border: isCurrent ? `2px solid ${c.color}` : '2px solid transparent',
                    '&:hover': { backgroundColor: c.bg, filter: 'brightness(0.95)' },
                  }}
                >
                  {s}{isCurrent ? '  (atual)' : ''}
                </Button>
              );
            })}
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#6b7280' }}>
            Ao marcar como "Finalizado", a data e hora são registradas. Se sair desse status, a data é limpa.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSituacaoDialog({ open: false })} sx={{ color: '#666' }}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Visualizar */}
      <Dialog open={viewDialog.open} onClose={() => setViewDialog({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1a1a1a', color: '#D4AF37', fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Detalhes da Reposição
          <IconButton onClick={() => setViewDialog({ open: false })} sx={{ color: '#D4AF37' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {viewDialog.record && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>Nº do Pedido</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{viewDialog.record.orderNumber}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>Situação</Typography>
                <Box sx={{ mt: 0.5 }}>{situacaoChip(viewDialog.record.situacao)}</Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>Adicionado em</Typography>
                <Typography variant="body2">{formatDateTimeBR(viewDialog.record.createdAt)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>Finalizado em</Typography>
                <Typography variant="body2" sx={{ color: viewDialog.record.finalizedAt ? '#15803d' : '#9ca3af', fontWeight: viewDialog.record.finalizedAt ? 600 : 400 }}>
                  {viewDialog.record.finalizedAt ? formatDateTimeBR(viewDialog.record.finalizedAt) : 'Ainda não finalizado'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>Observação</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{viewDialog.record.observation || '—'}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Reposicao;
